import React, { useMemo } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { useHistory, useRouteMatch } from "react-router-dom";

import { getRequestParams } from "fe-shared-transactions/src/helpers/transactionsRequestConverter";
import { Company, PermissionLevel } from "fe-shared/src/types/domain";
import useStorage from "fe-shared/src/hooks/useStorage";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import { getTransactionsFilterParams } from "fe-shared-transactions/src/helpers/queryString";
import useQueryFilters from "fe-shared-transactions/src/hooks/useQueryFilters";
import { PERMISSIONS, PAGE_SIZE, TRANSACTIONS_QUERY_KEY } from "consts";

import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";

import TransactionsListGrid from "../../components/TransactionsListGrid";

import * as api from "./modules/api";

interface Props {
  company: Company;
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
}

const TransactionsList = ({
  company,
  lastOpenedNumber,
  clearLastOpenedNumber,
}: Props) => {
  const { companyId, permissions, businessRoleCode } = company;
  const history = useHistory();
  const match = useRouteMatch();
  const canViewTransactionsList = hasPermission(permissions, {
    name: PERMISSIONS.transactionsList,
    level: PermissionLevel.Read,
  });

  const canCreateInvoice = hasPermission(permissions, {
    name: PERMISSIONS.invoiceCreate,
    level: PermissionLevel.Full,
  });

  const canViewTransactionDetails = hasPermission(permissions, {
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });

  const canExportTransactionsList = hasPermission(permissions, {
    name: PERMISSIONS.transactionsListExport,
    level: PermissionLevel.Full,
  });

  const canExportPictusJson = hasPermission(permissions, {
    name: PERMISSIONS.transactionListPictusExport,
    level: PermissionLevel.Full,
  });

  const [options, setOptions] = useStorage<DataGridOptions>(
    `webAppTransactionsOptions-${companyId}`,
    {
      sortDirection: "desc",
      sortField: "completedDate",
    },
    true
  );

  const [filters, setFilters] = useStorage<DataGridFilters>(
    `webAppTransactionsFilters-${companyId}`,
    {},
    true
  );

  useQueryFilters(setFilters, getTransactionsFilterParams, businessRoleCode);

  const requestParams = useMemo(() => getRequestParams(filters, options), [
    filters,
    options,
  ]);
  const transactionsQueryKey: QueryKey = [
    TRANSACTIONS_QUERY_KEY,
    requestParams,
    companyId,
  ];

  const transactions = useInfiniteQuery(
    transactionsQueryKey,
    api.getTransactions,
    {
      enabled: canViewTransactionsList,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getFetchMore: (lastGroup, allGroups) =>
        allGroups.length * PAGE_SIZE < lastGroup.total
          ? allGroups.length + 1
          : undefined,
    }
  );

  return canViewTransactionsList ? (
    <TransactionsListGrid
      businessRoleCode={businessRoleCode}
      company={company}
      filters={filters}
      options={options}
      setFilters={setFilters}
      setOptions={setOptions}
      transactions={transactions}
      lastOpenedNumber={lastOpenedNumber}
      transactionsQueryKey={transactionsQueryKey}
      clearLastOpenedNumber={clearLastOpenedNumber}
      canCreateInvoice={canCreateInvoice}
      canExportTransactionsList={canExportTransactionsList}
      canExportPictusJson={canExportPictusJson}
      onCellClicked={(e) => {
        const eventTag = "A";
        const currentNode = (e.event?.target as Element).nodeName;
        const isEventTagClicked = eventTag === currentNode;

        if (canViewTransactionDetails && !isEventTagClicked) {
          history.push(`${match.url}/${e.data.transactionNumber}`);
        }
      }}
    />
  ) : null;
};

export default TransactionsList;
